import React, {useState, useEffect, useRef} from "react";
import {useForm} from "react-hook-form";
import {useParams, useNavigate, useLocation} from "react-router-dom";
import FormHeader from "../../Common/FormHeader";
import ColorPalette from "../ColorPalette";
import IOSSwitch from "../../Common/IOSSwitch";
import {MdClose} from 'react-icons/md';
import secureStorage from "../../../utils/secureStorage";
import {api} from "../../../config/api";
import {URLS_UPLOADS} from "../../../config/url";
import Notification from "../../Common/Notification";

const EditCategoryForm = () => {
    const { categoryId } = useParams();
    const navigate = useNavigate();
    // Back to the catalogue list we came from (tab, filters and page kept)
    const returnTo = useLocation().state?.returnTo || "/catalogue";

    const [currentColor, setCurrentColor] = useState("#4DB6AC");
    const [showPalette, setShowPalette] = useState(false);
    const [categoryImage, setCategoryImage] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [notification, setNotification] = useState({
        show: false,
        type: 'success',
        message: '',
    });

    const {
        register,
        handleSubmit,
        formState: {errors},
        setValue,
        reset
    } = useForm();

    const formRef = useRef(null);

    // Function to show a notification
    const showNotification = (type, message) => {
        setNotification({
            show: true,
            type,
            message,
        });

        if (type === 'success') {
            setTimeout(() => {
                setNotification(prev => ({...prev, show: false}));
            }, 5000);
        }
    };

    // Function to hide notification
    const hideNotification = () => {
        setNotification(prev => ({...prev, show: false}));
    };

    // Fetch category data
    // Fetch category data
    useEffect(() => {
        const fetchCategory = async () => {
            try {
                setIsLoading(true);
                const accountId = await secureStorage.getItem("selectedAccount");

                const response = await api.get(`/categories/${categoryId}`, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json'
                    }
                });

                const category = response.data;
                console.log("Fetched category:", category);

                // Set form values based on API response structure
                setValue("name", category.name);
                setValue("description", category.description || "");
                setCurrentColor(category.color || "#4DB6AC");
                setIsActive(category.active === true); // Explicitly check for true

                // Handle image URL
                if (category.imageUrl) {
                    const imageUrl = category.imageUrl.startsWith('http')
                        ? category.imageUrl
                        : `${URLS_UPLOADS}${category.imageUrl}`;
                    setCategoryImage(imageUrl);
                }

                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching category:", error);
                showNotification('error', "Impossible de récupérer les informations de la catégorie");
                setIsLoading(false);
            }
        };

        fetchCategory();
    }, [categoryId, setValue]);

    const onSubmit = async (data) => {
        try {
            showNotification('loading', "Mise à jour de la catégorie...");

            // Create FormData object
            const formData = new FormData();

            // Add the ID to the form data
            formData.append("id", categoryId);

            // Add necessary fields with proper values
            formData.append("name", data.name);
            formData.append("color", currentColor);
            formData.append("description", data.description || "");
            formData.append("active", String(isActive));

            // Handle image upload - if there's a new image
            if (categoryImage instanceof File) {
                formData.append("image", categoryImage);
            } else if (typeof categoryImage === 'string' && categoryImage.startsWith('data:')) {
                // Convert base64 to file
                const response = await fetch(categoryImage);
                const blob = await response.blob();
                const file = new File([blob], "category-image.jpg", {type: "image/jpeg"});
                formData.append("image", file);
            }
            // If image is a URL from the server, we don't need to send it back

            // Get account ID
            const accountId = await secureStorage.getItem("selectedAccount");

            // Log the form data for debugging
            for (let pair of formData.entries()) {
                console.log(pair[0] + ': ' + pair[1]);
            }

            // Use POST method with /update endpoint
            const response = await api.post(`/categories/${categoryId}/update`, formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'multipart/form-data'
                }
            });

            console.log("Category updated:", response.data);
            showNotification('success', "Catégorie mise à jour avec succès");

            // Give time for notification to be seen, then return to previous page (preserves pagination)
            setTimeout(() => {
                navigate(returnTo);
            }, 1500);
        } catch (error) {
            console.error("Error updating category:", error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur s'est produite lors de la mise à jour";
            showNotification('error', `Erreur: ${errorMessage}`);
        }
    };


    const handleColorChange = (color) => {
        setCurrentColor(color);
        setShowPalette(false);
    };

    const handleImageChange = (image) => {
        setCategoryImage(image);
    };

    const togglePalette = () => {
        setShowPalette((prev) => !prev);
    };

    const toggleActive = () => {
        setIsActive((prev) => !prev);
    };

    const handleDeleteImage = () => {
        setCategoryImage(null);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary border-r-transparent align-[-0.125em]" role="status">
                        <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
                    </div>
                    <p className="mt-2 text-gray-600 dark:text-gray-300">Chargement de la catégorie...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            {/* Header */}
            <FormHeader title="Modifier la catégorie" back={returnTo} onSave={() => formRef.current && handleSubmit(onSubmit)()}/>

            <form
                ref={formRef}
                className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 py-8 space-y-8 w-full"
                onSubmit={handleSubmit(onSubmit)}
            >
                {/* Image/Color section */}
                <div className="flex flex-col items-center space-y-3">
                    <div className="text-center mb-2">
                        {categoryImage ? (
                            <div className="relative w-32 h-32 mx-auto">
                                <img
                                    src={categoryImage}
                                    alt="Category"
                                    className="w-32 h-32 object-cover rounded-lg shadow-sm cursor-pointer"
                                    onClick={togglePalette}
                                />
                                <button
                                    type="button"
                                    onClick={handleDeleteImage}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5
                                     shadow-sm hover:bg-red-600 transition-colors"
                                    aria-label="Delete image"
                                >
                                    <MdClose className="w-4 h-4"/>
                                </button>
                            </div>
                        ) : (
                            <div
                                className="w-32 h-32 rounded-lg flex items-center justify-center
                                text-white cursor-pointer shadow-sm hover:shadow-md transition-shadow mx-auto text-white"
                                onClick={togglePalette}
                                style={{backgroundColor: currentColor}}
                            >
                                Éditer
                            </div>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 block">
                            Cliquez pour modifier
                        </span>
                    </div>
                </div>

                {/* Form fields */}
                <div className="space-y-6">
                    <div>
                        <label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                            Nom :
                        </label>
                        <input
                            type="text"
                            id="name"
                            autoComplete="off"
                            {...register("name", {
                                required: "Le champ 'Nom' est obligatoire.",
                            })}
                            placeholder="Nom de la catégorie*"
                            className={`w-full px-4 py-2.5 border dark:bg-gray-800 dark:text-white dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${
                                errors.name
                                    ? "border-red-500 focus:ring-red-500 dark:border-red-700"
                                    : "border-gray-300 focus:ring-primary focus:border-primary dark:focus:border-primary"
                            }`}
                        />
                        {errors.name && (
                            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                        )}
                    </div>

                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="description" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                    Description (optionnelle) :
                                </label>
                                <textarea
                                    id="description"
                                    {...register("description")}
                                    rows="3"
                                    placeholder="Description de la catégorie"
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800
                                    dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-3">Actif :</span>
                        <IOSSwitch
                            checked={isActive}
                            onChange={toggleActive}
                        />
                    </div>
                </div>
            </form>

            {/* Color palette overlay - responsive & scrollable */}
            {showPalette && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={togglePalette}/>
                    <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
                        <div className="relative w-full sm:max-w-2xl bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col h-[85vh] sm:h-auto sm:max-h-[85vh]"
                             onClick={e => e.stopPropagation()}>
                            <ColorPalette
                                initialColor={currentColor}
                                onClose={togglePalette}
                                onColorChange={handleColorChange}
                                onImageChange={handleImageChange}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Modal */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />
        </div>
    );
};

export default EditCategoryForm;
